/**
 * AgentChatEngineContext — 대화 엔진 1개를 여러 뷰가 공유하기 위한 배선.
 *
 * 뷰는 둘이다:
 *   · AgentChatPanel — 우측 패널(전체 기능)
 *   · HomeAiCard     — 랜딩 홈의 AI 어시스턴트 카드(요약 뷰 + 시작점)
 * 어느 쪽에 입력해도 같은 대화가 양쪽에 실시간으로 보여야 하므로, 대화 상태는 뷰가 아니라
 * 여기(앱 셸)에 있다.
 *
 * ⚠️ 컨텍스트를 4채널로 쪼갠 이유는 성능이다. 이 Provider 는 SSE 델타마다 재렌더되므로,
 *   모든 값을 한 value 에 담으면 **홈 화면 전체가 델타마다 재렌더된다**.
 *     · EngineApiCtx      — 명령 팩토리. identity 영구 고정(빈 deps).
 *     · EngineMessagesCtx — 메시지 배열. 델타마다 변한다 → 메시지 목록만 구독.
 *     · EngineStatusCtx   — 진행/차단/신호. 턴 단위로 변한다 → 입력 영역·카드 셸.
 *     · EngineUiCtx       — 선택지·세션·액션 목록. 드물게 변한다.
 *   AgentChatHostContext.js 의 4분할과 같은 규약이다.
 *
 * 홈 카드가 idle(대화 시작 전) 상태에서 델타마다 재렌더되지 않도록, "대화가 있는가"는
 * messages 가 아니라 status.hasConversation(파생 boolean)으로 판정한다.
 */
import { createContext, useContext, useMemo } from "react";

import useAgentChatEngine from "@plannel/components/agent/useAgentChatEngine";

const EngineApiCtx = createContext(null);
const EngineMessagesCtx = createContext(null);
const EngineStatusCtx = createContext(null);
const EngineUiCtx = createContext(null);

const EMPTY_MESSAGES = [];
const EMPTY_STATUS = {
  typing: false, statusText: "", chatBusy: false, chatBusyBlocked: false, chatAborting: false,
  hasConversation: false, draftSignal: null, uiEpoch: 0, historyEpoch: 0,
};
const EMPTY_UI = {
  pendingChoices: null, choiceTrail: [], choiceInput: "", canGoBackChoice: false,
  feedbackGiven: {}, sessionId: null, sessions: null, conversationTitle: "", liveActions: [],
  actLabel: () => "", modCode: "GENERAL", standalone: true,
};
// Provider 밖(챗이 없는 렌더 경로)에서도 호출부가 터지지 않게 하는 무동작 폴백.
const NOOP_API = new Proxy({}, { get: () => () => undefined });

export function AgentChatEngineProvider({ children }) {
  const { messages, status, ui, apiFactory } = useAgentChatEngine();
  return (
    <EngineApiCtx.Provider value={apiFactory}>
      <EngineUiCtx.Provider value={ui}>
        <EngineStatusCtx.Provider value={status}>
          <EngineMessagesCtx.Provider value={messages}>{children}</EngineMessagesCtx.Provider>
        </EngineStatusCtx.Provider>
      </EngineUiCtx.Provider>
    </EngineApiCtx.Provider>
  );
}

/**
 * 뷰가 엔진에 명령한다. viewId 는 초안 되돌리기·포커스를 **그 뷰에만** 돌려주기 위한 식별자다
 * ("panel" | "homeCard"). 상수를 넘기므로 반환 객체 identity 는 영구 고정이다.
 */
export function useAgentChatEngineApi(viewId = "panel") {
  const factory = useContext(EngineApiCtx);
  return useMemo(() => (factory ? factory(viewId) : NOOP_API), [factory, viewId]);
}

/** 메시지 목록 — 구독하면 SSE 델타마다 재렌더된다. 목록을 실제로 그리는 곳에서만 쓸 것. */
export function useAgentChatMessages() {
  return useContext(EngineMessagesCtx) ?? EMPTY_MESSAGES;
}

/** 진행·차단 상태와 뷰 신호(draftSignal / uiEpoch / historyEpoch). */
export function useAgentChatStatus() {
  return useContext(EngineStatusCtx) ?? EMPTY_STATUS;
}

/** 선택지·세션 목록·액션 목록 등 자주 바뀌지 않는 값. */
export function useAgentChatUi() {
  return useContext(EngineUiCtx) ?? EMPTY_UI;
}
